import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid2, MobileStepper, Stack } from '@mui/material';

import { SwiperButton } from 'components/simple';

import { DashboardPortfolioRadialChart } from './DashboardPortfolioRadialChart';

const styles = {
  viewport: { overflow: 'hidden' },
  track: (activePage) => ({
    transform: `translateX(-${activePage * 100}%)`,
    transition: 'transform 350ms ease-out',
  }),
  slide: {
    flex: '0 0 100%',
    minWidth: 0,
  },
  stepper: ({ palette, spacing }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',

    '.MuiMobileStepper-dots': {
      gap: spacing(1),
    },

    '.MuiMobileStepper-dot': {
      width: 16,
      height: 16,
      backgroundColor: palette.colors.lightPale,
      border: `1px solid ${palette.colors.darkPale}`,

      '&.MuiMobileStepper-dotActive': {
        backgroundColor: palette.common.white,
      },
    },
  }),
};

export const DashboardPortfolioCharts = ({ chartsData, organisationId }) => {
  const [activePage, setActivePage] = useState(0);

  const onNextClick = () => {
    setActivePage((prevState) => Math.min(prevState + 1, chartsData.length - 1));
  };

  const onBackClick = () => {
    setActivePage((prevState) => Math.max(prevState - 1, 0));
  };

  return (
    <Stack gap={6}>
      <Box sx={styles.viewport}>
        <Stack direction="row" sx={styles.track(activePage)}>
          {chartsData.map(({ pageNumber, charts }) => (
            <Grid2
              key={pageNumber}
              container
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={3}
              sx={styles.slide}
            >
              {charts.map((chart) => (
                <Grid2 key={chart.projectName}>
                  <DashboardPortfolioRadialChart chartData={chart} organisationId={organisationId} />
                </Grid2>
              ))}
            </Grid2>
          ))}
        </Stack>
      </Box>

      {chartsData.length > 1 && (
        <MobileStepper
          steps={chartsData.length}
          position="static"
          activeStep={activePage}
          backButton={<SwiperButton disabled={activePage === 0} onClick={onBackClick} isBack />}
          nextButton={<SwiperButton disabled={activePage === chartsData.length - 1} onClick={onNextClick} />}
          sx={styles.stepper}
        />
      )}
    </Stack>
  );
};

DashboardPortfolioCharts.propTypes = {
  chartsData: PropTypes.arrayOf(
    PropTypes.shape({
      pageNumber: PropTypes.number.isRequired,
      charts: PropTypes.arrayOf(
        PropTypes.shape({
          performance: PropTypes.number,
          growing: PropTypes.number,
          participation: PropTypes.number,
          projectName: PropTypes.string,
          id: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  organisationId: PropTypes.string.isRequired,
};
